import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';

import { Trans, useTranslation } from 'react-i18next';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import ScoreCard from 'components/field/ScoreCard';
import Card from 'components/global/Card';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import {
	faCabinetFiling,
	faCircleInfo,
	faGasPump,
	faPersonCarryBox,
	faStore,
	faTruckFlatbed,
	faTruckRampBox
} from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import * as S from './ShippingDetailsModal.styled';

/**
 * @author @buunsal
 */

export const ShippingDetailsModal = NiceModal.create(({ data }) => {
	const { t } = useTranslation();
	const modal = useModal();
	const isBullet = data.serviceType === 'BULLET';
	const isImport = data.serviceType === 'IMPORT';

	const handleProceed = () => {
		modal.resolve({
			proceed: true
		});
		modal.remove();
	};

	return (
		<Modal fullwidth maxWidth="x-small" position="right" className="modal-shippingDetails" closeOnClickOutside>
			<ModalTitle>
				<Typography variant="p1">shippingDetails</Typography>
			</ModalTitle>

			<ModalContent style={{ width: '100%', height: '100%' }}>
				<S.ShippingDetailsCardContainer>
					<Card padding={0}>
						<S.ShippingDetailsCompanyHeader>
							<S.LogoWrapper>
								<Image url={data.logo} width="8rem" height="8rem" />
								<S.HeaderWrapper>
									<Typography variant="p2" fontWeight="700">
										{data.nameAr}
									</Typography>
									{data.score5 && <ScoreCard value={data.score5} disabled />}
									<Typography color="var(--content-3)">{data.serviceType}</Typography>
								</S.HeaderWrapper>
							</S.LogoWrapper>
							<S.PriceContainer>
								{data.preDiscountPrice !== data.price && (
									<Typography
										variant="p1"
										color="var(--red)"
										withCurrency
										currency={data.currency}
										style={{
											textDecoration: 'line-through'
										}}>
										{data.preDiscountPrice}
									</Typography>
								)}
								<Typography variant="h4" withCurrency currency={data.currency}>
									{data.price}
								</Typography>
							</S.PriceContainer>
						</S.ShippingDetailsCompanyHeader>
						<S.ShippingDetailsCardContent>
							{data.liabilityAmount !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">liability</Typography>
										<Tooltip title="liabilityDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
										{data.liabilityBreach && (
											<Badge
												variant="pill"
												color="yellow"
												text={`liabilityBreach.${data.liabilityBreach}`}
											/>
										)}
									</S.DetailItemLabel>
									<Typography withCurrency currency={data.currency} fontWeight="500" noWrap>
										<Trans
											i18nKey="shippingOptions.insurancePolicy"
											values={{
												liabilityValue: data.liabilityAmount,
												currency: t(data.currency)
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}
							{!isBullet && data.cutOffTime !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">pickupCutoffTime</Typography>
										<Tooltip title="pickupCutoffTimeDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
										{data.cutOffBreach && (
											<Badge
												variant="pill"
												color="yellow"
												text={`cutOffBreach.${data.cutOffBreach}`}
											/>
										)}
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										<Trans
											i18nKey="pickupCutoffTimeValue"
											values={{
												value: data.cutOffTime
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}
							{isBullet && data.bulletCutOff !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">bulletCutOffTime</Typography>
										<Tooltip title="bulletCutoffTimeDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										<Trans
											i18nKey="bulletCutOffTimeValue"
											values={{
												value: data.bulletCutOff
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}
							{!isBullet && data.customerWaitingTime !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">pickupByCustomerWaitingTime</Typography>
										<Tooltip title="pickupByCustomerWaitingTimeDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										<Trans
											i18nKey="pickupByCustomerWaitingTimeValue"
											values={{
												value: data.customerWaitingTime
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}
							{isBullet && data.waitingTimePickup !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">waitingTimePickup</Typography>
										<Tooltip title="waitingTimePickupDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										<Trans
											i18nKey="waitingTimePickupValue"
											values={{
												value: data.waitingTimePickup
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}

							{data.tempInsideWarehouse !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">tempInsideWarehouse</Typography>
										<Tooltip title="tempInsideWarehouseDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										{data.tempInsideWarehouse}
									</Typography>
								</S.DetailItem>
							)}
							{data.tempInsideVehicle !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">tempInsideVehicle</Typography>
										<Tooltip title="tempInsideVehicleDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										{data.tempInsideVehicle}
									</Typography>
								</S.DetailItem>
							)}
							{data.unsuccessfulReturnTime !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">unsuccessfulReturnTime</Typography>
										<Tooltip title="unsuccessfulReturnTimeDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										<Trans
											i18nKey="unsuccessfulReturnTimeValue"
											values={{
												value: data.unsuccessfulReturnTime
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}
							{!isBullet && data.liveTracking !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">liveTracking</Typography>
										<Tooltip title="liveTrackingDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										{data.liveTracking ? 'available' : 'notAvailable'}
									</Typography>
								</S.DetailItem>
							)}
						</S.ShippingDetailsCardContent>
					</Card>
					<Card padding={0}>
						<S.ShippingDetailsCardHeader>
							<Typography variant="p2" fontWeight="700">
								feesForAdditionalServices
							</Typography>
						</S.ShippingDetailsCardHeader>
						<S.ShippingDetailsCardContent>
							{data.extraWeightPerKg !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">
											<Trans
												i18nKey="extraChargeExceeding"
												values={{
													maxFreeWeight: data.maxFreeWeight
												}}
											/>
										</Typography>
										<Tooltip title="extraWeightChargeDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>

									<Typography fontWeight="500" noWrap>
										<Trans
											i18nKey="extraChargeExceedingValue"
											values={{
												value: data.extraWeightPerKg,
												currency: t(data.currency)
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}
							{data.codFee !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">CODCharge</Typography>
										<Tooltip title="CODChargeDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography withCurrency currency={data.currency} fontWeight="500" noWrap>
										{data.codFee}
									</Typography>
								</S.DetailItem>
							)}
							{data.cardOnDeliveryPercentage !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">cardOnDeliveryPercentage</Typography>
										<Tooltip title="cardOnDeliveryPercentageDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										<Trans
											i18nKey="cardOnDeliveryPercentageValue"
											values={{
												value: data.codFee,
												currency: t(data.currency),
												percentageValue: data.cardOnDeliveryPercentage
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}
							{!isBullet && data.failedDeliveryPrice !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">failedDeliveryPriceHeader</Typography>
										<Tooltip
											title={
												<Trans
													i18nKey="failedDeliveryPriceDescription"
													values={{
														failedDeliveryPrice: data.failedDeliveryPrice,
														price: data.price,
														currency: t(data.currency)
													}}
												/>
											}
											placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										{data.failedDeductPrice ? (
											<Trans
												i18nKey="failedDeductPrice"
												values={{
													value: data.failedDeductPrice,
													currency: t(data.currency)
												}}
											/>
										) : data.failedRefundPrice ? (
											<Trans
												i18nKey="failedRefundPrice"
												values={{
													value: data.failedRefundPrice,
													currency: t(data.currency)
												}}
											/>
										) : (
											'noAddtionalChargeOrRefund'
										)}
									</Typography>
								</S.DetailItem>
							)}
							{data.doorstepDeliveryFee !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">doorstepDeliveryFee</Typography>
										<Tooltip title="doorstepDeliveryFeeDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										<Trans
											i18nKey="doorstepDeliveryFeeValue"
											values={{
												value: data.doorstepDeliveryFee,
												currency: t(data.currency)
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}
							{data.disposalFee !== undefined && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">disposalFee</Typography>
										<Tooltip title="disposalFeeDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										<Trans
											i18nKey="disposalFeeValue"
											values={{
												value: data.disposalFee,
												currency: t(data.currency)
											}}
										/>
									</Typography>
								</S.DetailItem>
							)}
							{isBullet && data?.priceRangeList?.length > 0 && (
								<S.DetailItem>
									<S.DetailItemLabel>
										<Typography color="var(--content-3)">priceRangeList</Typography>
										<Tooltip title="priceRangeListDescription" placement="bottom">
											<Icon icon={faCircleInfo} color="var(--black)" />
										</Tooltip>
									</S.DetailItemLabel>
									<Typography fontWeight="500" noWrap>
										<S.PriceDetailsContainer>
											{data.priceRangeList.map((priceRange, index) => (
												<Typography key={index} fontWeight="500" noWrap>
													<Trans
														key={index}
														i18nKey="priceRangeListValue"
														values={{
															price: priceRange.price,
															greaterThanKm: priceRange.greaterThanKm,
															lessThanOrEqualKm: priceRange.lessThanOrEqualKm,
															currency: t(data.currency)
														}}
													/>
												</Typography>
											))}
										</S.PriceDetailsContainer>
									</Typography>
								</S.DetailItem>
							)}
						</S.ShippingDetailsCardContent>
					</Card>
					{(isBullet
						? data?.expectedPickupMins !== undefined ||
							data?.expectedDeliveryMins !== undefined ||
							data?.maxKm !== undefined ||
							data?.fuelSurcharge !== undefined
						: data.estimatedPickupDate !== undefined ||
							data.estimatedDeliveryDate !== undefined ||
							(data.dropOffCutOff !== undefined &&
								data.estimatedDropOffDate !== undefined &&
								data.estimatedDropOffDeliveryDate !== undefined) ||
							data.nearestBranch !== undefined ||
							data.checkAllBranches !== undefined ||
							data.expectedDeliveryDescription !== undefined ||
							data.expectedDropOffDescription !== undefined ||
							data.fuelSurcharge !== undefined ||
							data.lockerList?.length > 0) && (
						<Card padding={0}>
							<S.ShippingDetailsCardHeader>
								<Typography variant="p2" fontWeight="700">
									additionalInformation
								</Typography>
							</S.ShippingDetailsCardHeader>
							{(data.estimatedPickupDate !== undefined || data.estimatedDeliveryDate !== undefined) && (
								<>
									<S.ShippingDetailsCardContent>
										{data.estimatedPickupDate !== undefined && (
											<S.DetailItemWithIcon>
												<Icon icon={faTruckFlatbed} color="var(--black)" />
												<S.DetailItem>
													<S.DetailItemLabel>
														<Typography color="var(--content-3)">
															expectedPickupBy
														</Typography>
														<Tooltip title="expectedPickupByDescription" placement="bottom">
															<Icon icon={faCircleInfo} color="var(--black)" />
														</Tooltip>
													</S.DetailItemLabel>
													<Typography fontWeight="500" noWrap>
														{dayjs(data.estimatedPickupDate).format('dddd DD.MM.YYYY')}
													</Typography>
												</S.DetailItem>
											</S.DetailItemWithIcon>
										)}
										{data.estimatedDeliveryDate !== undefined && (
											<S.DetailItem>
												<S.DetailItemLabel>
													<Typography color="var(--content-3)">
														expectedDeliveryAfterPickup
													</Typography>
													<Tooltip
														title="expectedDeliveryAfterPickupDescription"
														placement="bottom">
														<Icon icon={faCircleInfo} color="var(--black)" />
													</Tooltip>
												</S.DetailItemLabel>
												<Typography fontWeight="500" noWrap>
													{dayjs(data.estimatedDeliveryDate).format('dddd DD.MM.YYYY')}
												</Typography>
											</S.DetailItem>
										)}
									</S.ShippingDetailsCardContent>
									{((data.dropOffCutOff !== undefined &&
										data.estimatedDropOffDate !== undefined &&
										data.estimatedDropOffDeliveryDate !== undefined) ||
										data.nearestBranch !== undefined) && (
										<S.DividerContainer>
											<S.Divider />
										</S.DividerContainer>
									)}
								</>
							)}

							{data.dropOffCutOff !== undefined &&
								data.estimatedDropOffDate !== undefined &&
								data.estimatedDropOffDeliveryDate !== undefined && (
									<S.SlaDetailsCardContent>
										<S.DetailItemWithIcon>
											<Icon icon={faPersonCarryBox} color="var(--black)" />
											<S.DetailItem>
												<S.DetailItemLabel>
													<Typography color="var(--content-3)">
														expectedDeliveryAfterDropoff
													</Typography>
												</S.DetailItemLabel>
												<Typography fontWeight="500" color="var(--content-3)" noWrap>
													<Trans
														i18nKey="deliveryAfterDropoffContent"
														values={{
															dropoffHour: dayjs(data.dropOffCutOff, 'HH:mm').format(
																'HH:mm A'
															),
															dropoffDate: dayjs(data.estimatedDropOffDate).format(
																'dddd, DD.MM.YYYY'
															),
															dropoffDeliveryDate: dayjs(
																data.estimatedDropOffDeliveryDate
															).format('dddd, DD.MM.YYYY')
														}}
														components={{
															blackText: <span style={{ color: 'var(--black)' }} />
														}}
													/>
												</Typography>
											</S.DetailItem>
										</S.DetailItemWithIcon>
									</S.SlaDetailsCardContent>
								)}

							{data.nearestBranch !== undefined && (
								<S.SlaDetailsCardContent>
									<S.DetailItemWithIcon>
										<Icon icon={isImport ? faTruckRampBox : faStore} color="var(--black)" />
										<S.DetailItem>
											{isImport ? (
												<S.ExpectedDescriptions>
													{data.expectedDeliveryDescription && (
														<>
															<S.DetailItemLabel>
																<Typography color="var(--content-3)">
																	expectedDeliveryHeader
																</Typography>
																<Tooltip
																	title="expectedDeliveryDescription"
																	placement="bottom">
																	<Icon icon={faCircleInfo} color="var(--black)" />
																</Tooltip>
															</S.DetailItemLabel>
															<Typography
																fontWeight="500"
																color="var(--content-3)"
																noWrap>
																{data.expectedDeliveryDescription}
															</Typography>
														</>
													)}
													{data.expectedDropOffDescription && (
														<>
															<S.DetailItemLabel>
																<Typography color="var(--content-3)">
																	dropoffHeader
																</Typography>
																<Tooltip
																	title="dropoffHeaderDescription"
																	placement="bottom">
																	<Icon icon={faCircleInfo} color="var(--black)" />
																</Tooltip>
															</S.DetailItemLabel>
															<Typography
																fontWeight="500"
																color="var(--content-3)"
																noWrap>
																{data.expectedDropOffDescription}
															</Typography>
														</>
													)}
												</S.ExpectedDescriptions>
											) : (
												<>
													<S.DetailItemLabel>
														<Typography color="var(--content-3)">
															nearestDropoffBranch
														</Typography>
													</S.DetailItemLabel>
													{data.nearestBranch.name && (
														<Typography fontWeight="500" color="var(--content-3)" noWrap>
															<Trans
																i18nKey="nearestBranchName"
																values={{
																	branchName: data.nearestBranch.name
																}}
																components={{
																	blackText: <S.BlackText />
																}}
															/>
														</Typography>
													)}
													{data.nearestBranch.address && (
														<Typography fontWeight="500" color="var(--content-3)" noWrap>
															<Trans
																i18nKey="newNearestBranchAddress"
																values={{
																	address: `${data.nearestBranch.address} ${data.nearestBranch.city ?? ''} ${data.nearestBranch.country ?? ''}`
																}}
																components={{
																	blackText: <S.BlackText />,
																	branchLink:
																		data.nearestBranch.lat &&
																		data.nearestBranch.lon ? (
																			<Button
																				label="navigateToBranch"
																				variant="outline"
																				css={css`
																					display: inline-flex;
																					text-decoration: underline;
																					border: none !important;
																				`}
																				onClick={() => {
																					window.open(
																						`https://www.google.com/maps/search/?api=1&query=${data.nearestBranch.lat},${data.nearestBranch.lon}`
																					);
																				}}
																			/>
																		) : (
																			<></>
																		)
																}}
															/>
														</Typography>
													)}
													{data.nearestBranch.workingHours && (
														<Typography fontWeight="500" color="var(--content-3)" noWrap>
															<Trans
																i18nKey="nearestBranchWorkingHours"
																values={{
																	workingHours: data.nearestBranch.workingHours
																}}
																components={{
																	blackText: <S.BlackText />
																}}
															/>
														</Typography>
													)}
												</>
											)}
										</S.DetailItem>
									</S.DetailItemWithIcon>
								</S.SlaDetailsCardContent>
							)}

							{data.checkAllBranches !== undefined && (
								<S.ShippingDetailsCardContent>
									<S.DetailItem>
										<Button
											label={
												data.serviceType === 'LOCKER_DELIVERY'
													? 'checkAllLockers'
													: 'checkAllBranches'
											}
											icon={data.serviceType === 'LOCKER_DELIVERY' ? faCabinetFiling : faStore}
											variant="outline"
											css={css`
												text-decoration: underline;
												border: none !important;
											`}
											component="a"
											href={data.checkAllBranches}
											target="_blank"
										/>
									</S.DetailItem>
								</S.ShippingDetailsCardContent>
							)}
							<S.ShippingDetailsCardContent>
								{data.expectedPickupMins !== undefined && (
									<S.DetailItemWithIcon>
										<Icon icon={faTruckFlatbed} color="var(--black)" />
										<S.DetailItem>
											<S.DetailItemLabel>
												<Typography color="var(--content-3)">expectedPickupMins</Typography>
												<Tooltip title="expectedPickupMinsDescription" placement="bottom">
													<Icon icon={faCircleInfo} color="var(--black)" />
												</Tooltip>
											</S.DetailItemLabel>
											<Typography fontWeight="500" noWrap>
												<Trans
													i18nKey="expectedPickupMinsValue"
													values={{ value: data.expectedPickupMins }}
												/>
											</Typography>
										</S.DetailItem>
									</S.DetailItemWithIcon>
								)}
								{data.expectedDeliveryMins !== undefined && (
									<S.DetailItem>
										<S.DetailItemLabel>
											<Typography color="var(--content-3)">expectedDeliveryMins</Typography>
											<Tooltip title="expectedDeliveryMinsDescription" placement="bottom">
												<Icon icon={faCircleInfo} color="var(--black)" />
											</Tooltip>
										</S.DetailItemLabel>
										<Typography fontWeight="500" noWrap>
											<Trans
												i18nKey="expectedDeliveryMinsValue"
												values={{ value: data.expectedDeliveryMins }}
											/>
										</Typography>
									</S.DetailItem>
								)}

								{data.maxKm !== undefined && (
									<S.SlaDetailsCardContent leftPadded>
										<S.DetailItem>
											<S.DetailItemLabel>
												<Typography color="var(--content-3)">maxKm</Typography>
												<Tooltip title="maxKmDescription" placement="bottom">
													<Icon icon={faCircleInfo} color="var(--black)" />
												</Tooltip>
											</S.DetailItemLabel>
											<Typography fontWeight="500" noWrap>
												<Trans i18nKey="maxKmValue" values={{ value: data.maxKm }} />
											</Typography>
										</S.DetailItem>
									</S.SlaDetailsCardContent>
								)}

								{data.fuelSurcharge !== undefined && (
									<S.DetailItemWithIcon>
										<Icon icon={faGasPump} color="var(--black)" />
										<S.DetailItem>
											<S.DetailItemLabel>
												<Typography color="var(--content-3)">fuelSurcharge</Typography>
												<Tooltip title="fuelSurchargeDescription" placement="bottom">
													<Icon icon={faCircleInfo} color="var(--black)" />
												</Tooltip>
											</S.DetailItemLabel>
											<Typography fontWeight="500" noWrap>
												<Trans
													i18nKey="fuelSurchargeValue"
													values={{ value: data.fuelSurcharge }}
												/>
											</Typography>
										</S.DetailItem>
									</S.DetailItemWithIcon>
								)}

								{data.lockerList?.length > 0 && (
									<S.DetailItemWithIcon>
										<Icon icon={faCabinetFiling} color="var(--black)" />
										<S.DetailItem>
											<S.DetailItemLabel>
												<Typography color="var(--content-3)">
													lockerCabinetDimensions
												</Typography>
											</S.DetailItemLabel>
											<Typography fontWeight="500" noWrap className="my-4">
												lockerCabinetDimensionsDescriptions
											</Typography>
											{data.lockerList.map((locker) => (
												<Typography fontWeight="700" noWrap key={locker.id}>
													{t(locker.name)} - {locker.length}x{locker.height}x{locker.width}
												</Typography>
											))}
										</S.DetailItem>
									</S.DetailItemWithIcon>
								)}
							</S.ShippingDetailsCardContent>
						</Card>
					)}
				</S.ShippingDetailsCardContainer>
			</ModalContent>

			<ModalActions>
				<Button
					variant="blackOutline"
					label="cancel"
					onClick={modal.remove}
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
				/>

				<Button label="proceed" icon={faChevronRight} iconPos="right" onClick={handleProceed} />
			</ModalActions>
		</Modal>
	);
});

export default ShippingDetailsModal;
